// HospitalityDetailDialog.js
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Badge } from '../../ui/badge';
import { Pencil, Upload, Camera, DollarSign, Gift, Users, Star, Building2, EyeOff, Calendar, CheckCircle2, FileText, User } from 'lucide-react';
import { useHospitalityDetail } from './hooks/useHospitalityDetail';
import ActivityDialogFooter from './shared/ActivityDialogFooter';
import FilePreviewDialog from './shared/FilePreviewDialog';

export const HospitalityDetailDialog = ({ open, onOpenChange, hospitality, onSuccess, associations = [], usersMap = {}, onHospitalityUpdate, canEditAccount }) => {
  const {
    isEditing, setIsEditing,
    saving,
    uploadingReceipt, uploadingPhotos,
    filePreviewOpen, setFilePreviewOpen,
    previewFileUrl, previewFileType, previewFileId,
    isEditingFile, setIsEditingFile,
    editFileDescription, setEditFileDescription,
    updatingFile,
    formData, setFormData,
    receiptFile, photoFiles, setReceiptFile, setPhotoFiles,
    handleSave, handleDelete, handleFileUpload, cancelEdit,
    openFileInNewTab, downloadFileFromUrl,
    handleDeleteFile, handleUpdateFileDescription,
    handleRotateFile, isRotatingFile,
    getCategoryLabel,
    canEdit, recipientAssoc,
  } = useHospitalityDetail(hospitality, { associations, onHospitalityUpdate, canEditAccount, onOpenChange, onSuccess });

  if (!hospitality) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="hospitality-detail-dialog">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle>Hospitality & Gift Details</DialogTitle>
          {!isEditing && canEdit && (
            <Button variant="ghost" size="icon" onClick={() => setIsEditing(true)} data-testid="edit-hosp-btn">
              <Pencil className="w-4 h-4" />
            </Button>
          )}
        </DialogHeader>

        {isEditing ? (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(val) => setFormData({ ...formData, category: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gift">Gift</SelectItem>
                    <SelectItem value="hospitality_dining">Dining</SelectItem>
                    <SelectItem value="hospitality_experience">Experience</SelectItem>
                    <SelectItem value="hospitality_travel">Travel</SelectItem>
                    <SelectItem value="hospitality_meeting">Meeting</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-hosp-date">Date</Label>
                <Input
                  id="edit-hosp-date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-hosp-summary">Summary *</Label>
              <Input
                id="edit-hosp-summary"
                value={formData.short_summary}
                onChange={(e) => setFormData({ ...formData, short_summary: e.target.value })}
                placeholder="e.g., Birthday gift, Dinner at Keeneland"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select 
                  value={formData.currency} 
                  onValueChange={(val) => setFormData({ ...formData, currency: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="AUD">AUD</SelectItem>
                    <SelectItem value="CAD">CAD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-hosp-value">Monetary Value</Label>
                <Input
                  id="edit-hosp-value"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.monetary_value}
                  onChange={(e) => setFormData({ ...formData, monetary_value: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div className="space-y-2">
                <Label>Approval Status</Label>
                <Select 
                  value={formData.approval_status} 
                  onValueChange={(val) => setFormData({ ...formData, approval_status: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="na">N/A</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Recipient (if different)</Label>
              <Select 
                value={formData.recipient_association_id || 'none'} 
                onValueChange={(val) => setFormData({ ...formData, recipient_association_id: val === 'none' ? '' : val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select recipient" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Same as account</SelectItem>
                  {associations.map(assoc => (
                    <SelectItem key={assoc.id} value={assoc.id}>
                      {assoc.entityName} - {assoc.relationship?.replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-hosp-details">Details</Label>
              <Textarea
                id="edit-hosp-details"
                value={formData.details}
                onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                placeholder="Additional details..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Receipt (optional)</Label>
              <div className="flex gap-2">
                <input
                  type="file"
                  id="edit-hosp-receipt-upload"
                  accept="image/*,application/pdf"
                  onChange={(e) => handleFileUpload(e, 'receipt')}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('edit-hosp-receipt-upload').click()}
                  disabled={uploadingReceipt}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {receiptFile ? 'Replace Receipt' : 'Upload Receipt'}
                </Button>
                
                {receiptFile && (
                  <span className="text-xs text-muted-foreground flex items-center gap-2">
                    ✓ Receipt uploaded
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setReceiptFile(null)}
                    >
                      Remove
                    </Button>
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Photos (optional)</Label>
              <div className="flex gap-2">
                <input
                  type="file"
                  id="edit-hosp-photo-upload"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleFileUpload(e, 'photo')}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('edit-hosp-photo-upload').click()}
                  disabled={uploadingPhotos}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Photos
                </Button>
                
                <input
                  type="file"
                  id="edit-hosp-photo-camera"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => handleFileUpload(e, 'photo')}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('edit-hosp-photo-camera').click()}
                  disabled={uploadingPhotos}
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Take Photo
                </Button>
              </div>
              {photoFiles.length > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-muted-foreground">
                    {photoFiles.length} photo(s)
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setPhotoFiles([])}
                  >
                    Clear all
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-1">
            <div className="p-4 bg-muted/30 rounded">
              <div className="flex justify-between items-center mb-2">
                <Badge variant="secondary" className="text-xs flex items-center gap-1">
                  <Gift className="w-3 h-3" />
                  {getCategoryLabel(hospitality.category)}
                </Badge>
                <span className="text-muted-foreground text-xs">
                  {new Date(hospitality.date).toLocaleDateString()}
                </span>
              </div>
              
              <p className="font-medium mb-3">{hospitality.short_summary}</p>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Date:</span>
                <span>{new Date(hospitality.date).toLocaleDateString()}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Value:</span>
                <span>{hospitality.currency || 'USD'} {hospitality.monetary_value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</span>
              </div>
              
              {recipientAssoc && (
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Recipient:</span>
                  <span className="font-medium">{recipientAssoc.entityName}</span>
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Approval:</span>
                <Badge variant={hospitality.approval_status === 'yes' ? 'default' : 'outline'} className="text-xs capitalize">
                  {hospitality.approval_status === 'na' ? 'N/A' : hospitality.approval_status}
                </Badge>
              </div>
            </div>
          </div>

          {hospitality.details && (
            <div className="space-y-1 pt-2">
              <Label className="text-sm font-medium">Details</Label>
              <p className="text-sm whitespace-pre-wrap text-muted-foreground">{hospitality.details}</p>
            </div>
          )}

          {hospitality.receipt_file_id && (
            <div className="pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => openFileInNewTab(hospitality.receipt_file_id)}
              >
                <FileText className="w-4 h-4 mr-2" />
                View Receipt
              </Button>
            </div>
          )}

          {hospitality.photo_file_ids && hospitality.photo_file_ids.length > 0 && (
            <div className="space-y-1 pt-2">
              <Label className="text-sm font-medium">Photos ({hospitality.photo_file_ids.length})</Label>
              <div className="grid grid-cols-3 gap-2">
                {hospitality.photo_file_ids.map((photoId, idx) => (
                  <div 
                    key={idx}
                    className="aspect-square bg-muted rounded cursor-pointer hover:opacity-80"
                    onClick={() => openFileInNewTab(photoId)}
                  >
                    <img 
                      src={`${window.location.origin}/api/files/${photoId}`}
                      alt={`Photo ${idx + 1}`}
                      className="w-full h-full object-cover rounded"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-muted"><svg class="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Created Info */}
          {!isEditing && hospitality.created_at && (
            <div className="pt-3 border-t text-xs text-muted-foreground">
              Created: {new Date(hospitality.created_at).toLocaleDateString()} 
              {hospitality.created_by && usersMap[hospitality.created_by] && (
                <> by {usersMap[hospitality.created_by].name}</>
              )}
            </div>
          )}
          </div>
        )}

        <ActivityDialogFooter
          isEditing={isEditing}
          saving={saving}
          onSave={handleSave}
          onCancel={cancelEdit}
          onDelete={handleDelete}
          onClose={() => onOpenChange(false)}
          deleteTestId="delete-hosp-btn"
        />
      </DialogContent>

      <FilePreviewDialog
        open={filePreviewOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setFilePreviewOpen(false);
            setIsEditingFile(false);
            setEditFileDescription('');
            if (previewFileUrl) URL.revokeObjectURL(previewFileUrl);
          } else {
            setFilePreviewOpen(true);
          }
        }}
        previewFileUrl={previewFileUrl}
        previewFileType={previewFileType}
        isEditingFile={isEditingFile}
        setIsEditingFile={setIsEditingFile}
        editFileDescription={editFileDescription}
        setEditFileDescription={setEditFileDescription}
        updatingFile={updatingFile}
        onDeleteFile={handleDeleteFile}
        onUpdateDescription={handleUpdateFileDescription}
        onDownload={() => downloadFileFromUrl(previewFileUrl, editFileDescription || 'file')}
        onResetDescription={() => {
          setIsEditingFile(false);
          setEditFileDescription(previewFileId === hospitality?.receipt_file_id ? 'Receipt' : 'Photo');
        }}
        onRotate={previewFileType?.startsWith('image/') ? handleRotateFile : undefined}
        isRotating={isRotatingFile}
      />
    </Dialog>
  );
};

